<script setup>
const { application } = defineProps({
  application: Object,
});

const toast = useToast();
const uploadedReceiptFile = inject("uploadedReceiptFile");

const onFileSelect = async (file) => {
  const receiptFile = file;
  uploadedReceiptFile.value = receiptFile;
  console.log("File uploaded:", uploadedReceiptFile.value);
};
const onFileRemove = (file) => {
  uploadedReceiptFile.value = null;
  console.log(
    "File removed, reset uploadedReceiptFile:",
    uploadedReceiptFile.value
  );
};
const onFileUploadError = (msg) => {
  toast.add({
    severity: "error",
    summary: msg,
    life: 3000,
  });
};

const handleContactUnilinks = () => {
  const email = "info@unilinks.co";
  const subject = encodeURIComponent(
    "Issue with Viewing Offer and Making Payment"
  );
  const body = encodeURIComponent(
    `Dear UniLinks Support Team,\n\nI am facing an issue while trying to view my offer and make a payment for my university application. The issue I am experiencing is:\n\n[Please describe your issue here]\n\nCould you please assist me in resolving this?\n\nBest regards,\n[Your Name]`
  );

  window.open(
    `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`,
    "_blank"
  );
};

const getDocDownloadUrl = (key) => {
  const doc = application.studentApplicationDocuments?.find(
    (doc) => doc.label === key
  );
  const url = doc?.media?.at(0)?.original_url;
  return url || null;
};
</script>

<template>
  <div class="bg-white rounded-xl drop-shadow-sm max-w-[600px]">
    <div class="flex gap-3 px-8 py-5 border-b border-slate-200">
      <Avatar size="large" :label="application?.university?.name?.at(0)" />
      <div>
        <h3 class="font-medium text-black">
          {{ application?.university?.name }}
        </h3>
        <p class="text-sm">
          <span class="pr-3 border-r border-slate-200">{{
            application?.program?.name
          }}</span>
          <span class="px-3 border-r border-slate-200">{{
            application?.university?.university_address?.city
          }}</span>
          <span class="px-3">
            {{ application?.university?.ranking?.name }}
            {{ application?.university?.ranking?.number }}</span
          >
          <i class="pi pi-star-fill text-[#EAB308]"></i>
        </p>
      </div>
    </div>
    <figure
      v-if="
        application?.status === 'university_approved' ||
        application?.status === 'payment_success'
      "
      class="w-full h-[174px] px-8 pt-5"
    >
      <img
        src="~/assets/img/application-steps/offer.png"
        class="w-full h-full object-cover rounded-xl"
      />
    </figure>
    <div
      class="px-8 py-8 space-y-4 text-sm"
      v-if="
        application?.status === 'university_approved' ||
        application?.status === 'payment_success'
      "
    >
      <p class="text-surface-900">Hello, {{ application?.student?.name }} !</p>
      <p class="text-surface-900">
        Congratulation 🎉 Your application has been approved by
        <span class="text-tertiary-600 font-bold">{{
          application?.university?.name
        }}</span
        >. You can now decide to accept the offer or negotiate with UniLinks. If
        you accept, please make the payment and upload the receipt
      </p>

      <!-- acceptance letter and invoice file ui -->
      <div class="flex items-center gap-x-4 pt-6">
        <div class="flex-1 flex items-center bg-gray-100 p-2 rounded-xl">
          <img src="~/assets/img/pdf.png" alt="PDF" class="w-12 h-12 mr-1" />
          <div class="text-left">
            <p class="font-semibold text-black">Acceptance Letter</p>
            <a
              :href="getDocDownloadUrl('acceptance_letter')"
              target="_blank"
              class="text-blue-500 hover:text-blue-600"
              download
            >
              Download
            </a>
          </div>
        </div>

        <div class="flex-1 flex items-center bg-gray-100 p-2 rounded-xl">
          <img src="~/assets/img/pdf.png" alt="PDF" class="w-12 h-12 mr-1" />
          <div class="text-left">
            <p class="font-semibold text-black">Invoice</p>
            <a
              :href="getDocDownloadUrl('invoice')"
              target="_blank"
              class="text-blue-500 hover:text-blue-600"
              download
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </div>

    <div
      class="px-8 pt-5 pb-8 space-y-4 text-sm"
      v-else-if="
        application?.status === 'student_offer_accepted' ||
        application?.status === 'payment_received'
      "
    >
      <p class="text-surface-900">
        To complete the offer process, please make the payment. Bank details are
        provided in the invoice. After payment, kindly upload your receipt. If
        you face any issues,
        <button
          type="button"
          class="text-tertiary-600 font-bold hover:text-tertiary-700"
          @click="handleContactUnilinks"
        >
          Click here
        </button>
        contact to UniLinks
      </p>

      <!-- acceptance letter and invoice file ui -->
      <div class="flex items-center gap-x-4 pt-6">
        <div class="flex-1 flex items-center bg-gray-100 p-2 rounded-xl">
          <img src="~/assets/img/pdf.png" alt="PDF" class="w-12 h-12 mr-1" />
          <div class="text-left">
            <p class="font-semibold text-black">Acceptance Letter</p>
            <a
              :href="getDocDownloadUrl('acceptance_letter')"
              target="_blank"
              class="text-blue-500 hover:text-blue-600"
              download
            >
              Download
            </a>
          </div>
        </div>

        <div class="flex-1 flex items-center bg-gray-100 p-2 rounded-xl">
          <img src="~/assets/img/pdf.png" alt="PDF" class="w-12 h-12 mr-1" />
          <div class="text-left">
            <p class="font-semibold text-black">Invoice</p>
            <a
              :href="getDocDownloadUrl('invoice')"
              target="_blank"
              class="text-blue-500 hover:text-blue-600"
              download
            >
              Download
            </a>
          </div>
        </div>
      </div>

      <div class="pt-5" v-if="application?.status === 'student_offer_accepted'">
        <CustomFileUpload
          :maxSizeMB="2"
          @onUpload="onFileSelect"
          @onRemove="onFileRemove"
          @onError="onFileUploadError"
        />

        <p class="text-xs text-muted mt-4">
          You need to upload the receipt before deadline. You can find the
          deadline in invoice detail.
        </p>
      </div>
    </div>
  </div>
</template>
